/**
 * Swap Program Withdraw Functions
 *
 * Swap operations now use the main program (junkTH...).
 * SOL earned from dust swaps goes to the main program's UserInfo PDA.
 * This module provides functions to withdraw from that PDA.
 */

import { Transaction, SystemProgram, Connection, PublicKey } from '@solana/web3.js';
import { AnchorProvider, Program, Idl, BN } from '@coral-xyz/anchor';
import bs58 from 'bs58';

import idl from './idl/junk_fun_program.json';

// Swap now uses main program (previously was separate: 3Nq7QZQCX4bbVdgq5FjPyvkSSzerCT5r65oi42pEUerW)
export const SWAP_PROGRAM_ID = new PublicKey('junkTHpAet6MuYuTfHej12Zh12aWeG6tXbB7hnLjTKi');

// Always use mainnet for swap program operations (Jupiter only works on mainnet)
// Use Helius (VITE_SOLANA_RPC_URL) for better reliability; the public RPC covers local dev without it
const MAINNET_RPC_URL =
  import.meta.env?.VITE_SOLANA_RPC_URL || 'https://api.mainnet-beta.solana.com';

/**
 * Get mainnet connection for swap program operations
 */
export function getSwapConnection(): Connection {
  return new Connection(MAINNET_RPC_URL, {
    commitment: 'confirmed',
    confirmTransactionInitialTimeout: 60000,
  });
}

/**
 * Get swap program instance
 */
export function getSwapProgram(provider: AnchorProvider): Program<Idl> {
  // Create IDL with swap program address
  const swapIdl = { ...idl, address: SWAP_PROGRAM_ID.toString() };
  return new Program(swapIdl as Idl, provider);
}

/**
 * Derive GlobalConfig PDA for swap program
 */
export function getSwapGlobalConfigPDA(): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('global_config')],
    SWAP_PROGRAM_ID
  );
}

/**
 * Derive UserInfo PDA for swap program
 */
export function getSwapUserInfoPDA(userPublicKey: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('user_info'), userPublicKey.toBuffer()],
    SWAP_PROGRAM_ID
  );
}

export interface SwapPdaBalance {
  pdaAddress: string;
  rawLamports: number;
  balanceField: number;
  withdrawable: number;
  rentExempt: number;
}

/**
 * Get swap program PDA balance details
 */
export async function getSwapPdaBalance(userPublicKey: PublicKey): Promise<SwapPdaBalance | null> {
  const connection = getSwapConnection();
  const [swapUserInfoPDA] = getSwapUserInfoPDA(userPublicKey);

  try {
    const accountInfo = await connection.getAccountInfo(swapUserInfoPDA);
    if (!accountInfo || !accountInfo.data) {
      return null;
    }

    // Parse balance field (8 bytes at offset 8, after discriminator)
    const dataView = new DataView(accountInfo.data.buffer, accountInfo.data.byteOffset);
    const balanceField = Number(dataView.getBigUint64(8, true));

    // Calculate rent-exempt minimum
    const rentExempt = await connection.getMinimumBalanceForRentExemption(accountInfo.data.length);
    const withdrawable = Math.max(0, accountInfo.lamports - rentExempt);

    return {
      pdaAddress: swapUserInfoPDA.toString(),
      rawLamports: accountInfo.lamports,
      balanceField,
      withdrawable,
      rentExempt,
    };
  } catch (error) {
    console.error('[SWAP-WITHDRAW] Error fetching swap PDA balance:', error);
    return null;
  }
}

/**
 * Build withdraw transaction for swap program
 */
export async function buildSwapWithdrawTx(
  provider: AnchorProvider,
  amountLamports: bigint
): Promise<Transaction> {
  if (amountLamports <= BigInt(0)) {
    throw new Error('Withdrawal amount must be greater than 0');
  }

  // Use mainnet connection for swap program
  const mainnetConnection = getSwapConnection();
  const mainnetProvider = new AnchorProvider(
    mainnetConnection,
    provider.wallet,
    { commitment: 'confirmed' }
  );

  const program = getSwapProgram(mainnetProvider);
  const wallet = provider.wallet.publicKey;

  if (!wallet) {
    throw new Error('Wallet not connected');
  }

  // Derive PDAs for swap program
  const [globalConfig] = getSwapGlobalConfigPDA();
  const [userInfo] = getSwapUserInfoPDA(wallet);

  console.log(`[SWAP-WITHDRAW] Building withdraw transaction:`);
  console.log(`  Amount: ${Number(amountLamports) / 1e9} SOL`);
  console.log(`  Swap Program: ${SWAP_PROGRAM_ID.toString()}`);
  console.log(`  GlobalConfig PDA: ${globalConfig.toString()}`);
  console.log(`  UserInfo PDA: ${userInfo.toString()}`);

  // Build instruction
  const ix = await program.methods
    .depositOrWithdraw(false, new BN(amountLamports.toString()))
    .accounts({
      user: wallet,
      globalConfig,
      userInfo,
      systemProgram: SystemProgram.programId,
    } as any)
    .instruction();

  const transaction = new Transaction().add(ix);

  return transaction;
}

/**
 * Sign and send swap program withdraw transaction
 */
export async function signAndSendSwapWithdraw(
  provider: AnchorProvider,
  transaction: Transaction
): Promise<string> {
  // Use mainnet connection
  const mainnetConnection = getSwapConnection();

  console.log(`[SWAP-WITHDRAW] Signing and sending transaction...`);

  // Get latest blockhash from mainnet
  const { blockhash, lastValidBlockHeight } = await mainnetConnection.getLatestBlockhash('confirmed');
  transaction.recentBlockhash = blockhash;
  transaction.feePayer = provider.wallet.publicKey;

  // Sign the transaction
  const signedTx = await provider.wallet.signTransaction(transaction);

  // Get signature
  const signature = signedTx.signature ? bs58.encode(Buffer.from(signedTx.signature)) : null;

  if (!signature) {
    throw new Error('Failed to get transaction signature');
  }

  console.log(`[SWAP-WITHDRAW] Transaction signature: ${signature}`);

  try {
    // Send to mainnet
    await mainnetConnection.sendRawTransaction(signedTx.serialize(), {
      skipPreflight: false,
      maxRetries: 3,
    });

    // Confirm on mainnet
    await mainnetConnection.confirmTransaction(
      {
        signature,
        blockhash,
        lastValidBlockHeight,
      },
      'confirmed'
    );

    console.log(`[SWAP-WITHDRAW] Transaction confirmed: ${signature}`);
    return signature;
  } catch (error: any) {
    const errorMessage = error?.message || String(error);
    const isAlreadyProcessed = errorMessage.includes('already been processed') ||
                               errorMessage.includes('already processed');

    if (isAlreadyProcessed) {
      const status = await mainnetConnection.getSignatureStatus(signature);
      if (status?.value?.confirmationStatus &&
          (status.value.confirmationStatus === 'confirmed' || status.value.confirmationStatus === 'finalized') &&
          !status.value.err) {
        console.log(`[SWAP-WITHDRAW] Transaction was already confirmed: ${signature}`);
        return signature;
      }
    }

    console.error(`[SWAP-WITHDRAW] Transaction failed:`, error);
    throw error;
  }
}
